/**
 * Traduit le palier retenu en une variante de produit de pack.
 *
 * Un pack est un produit à part entière, jamais affiché, qui n'existe que pour
 * porter un prix : Shopify ne facture que ce qui est écrit sur une variante.
 * La fiche n'en montre rien — le sélecteur de paliers reste de l'interface, et
 * c'est ici que le palier choisi devient une ligne de panier.
 *
 * Une seule ligne part au panier, au prix exact du pack. Plus de remise à
 * appliquer, plus de trois t-shirts séparés qu'un client peut décomposer en
 * modifiant une quantité, et plus de prix à tenir synchronisé entre la fiche et
 * l'admin : il n'y a qu'une source.
 *
 * C'est aussi ici que passe la composition libre (assets/pack-composer.js) : le
 * panneau émet `pack:add` avec sa ligne de Pack perso, et elle part par le même
 * chemin que les packs. Un seul endroit parle au panier.
 *
 * L'interception se fait en capture sur `window`, et c'est le seul endroit qui
 * marche : Horizon délègue ses `on:submit` sur `document` en capture lui aussi
 * (assets/component.js), et son écouteur est enregistré avant le nôtre. Au même
 * nœud, dans la même phase, l'ordre d'inscription décide — et `stopPropagation`
 * n'arrête pas les écouteurs voisins du même nœud.
 *
 * `window` précède `document` dans le trajet de capture. On passe donc avant, et
 * on interrompt le trajet avant qu'il n'atteigne le délégué du thème.
 *
 * 🛑 Rien de cosmétique ne doit s'exécuter entre `preventDefault()` et la
 * requête d'ajout. L'animation native du bouton a été tentée ici : appelée hors
 * de son flux, elle lève sur une référence absente, et l'exception emportait
 * l'ajout avec elle. Un défaut de retour visuel est acceptable, un panier qui
 * ne se remplit pas ne l'est pas.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

/** @typedef {{ id: number, available: boolean, options: string[] }} PackVariant */
/** @typedef {{ options: string[], variants: PackVariant[] }} PackMap */
/** @typedef {{ id: number, quantity: number, properties?: Record<string, string> }} PackLine */

const normalise = (value) => (value || '').toString().trim().toLowerCase();

/** @returns {HTMLInputElement | null} */
const selectedTier = () => document.querySelector('input[name="pack_tier"]:checked');

/** @returns {HTMLInputElement | null} */
const selectedDuo = () => document.querySelector('input[name="pack_duo"]:checked');

/**
 * Carte des variantes du produit associé au palier.
 * @param {HTMLInputElement} tier
 * @returns {PackMap | null}
 */
function readPackMap(tier) {
  const node = document.querySelector(`[data-pack-product-map="${CSS.escape(tier.value)}"]`);
  if (!node?.textContent) return null;

  try {
    return JSON.parse(node.textContent);
  } catch {
    return null;
  }
}

/**
 * Valeurs cochées dans le sélecteur natif, par nom d'option normalisé.
 * @returns {Map<string, string>}
 */
function selectedOptions() {
  const selection = new Map();

  for (const input of document.querySelectorAll('.variant-option input[type="radio"]:checked')) {
    if (!(input instanceof HTMLInputElement)) continue;
    const name = input.dataset.optionName;
    if (name) selection.set(normalise(name), input.value);
  }

  return selection;
}

/**
 * Contenu désigné par une vignette pour le palier retenu. Une vignette de
 * coloris vaut ce coloris autant de fois que le palier compte de pièces — le
 * même « Noir » part en 2X Noir ou en 5X Noir. Une vignette à contenu fixe vaut
 * son contenu.
 * @param {HTMLInputElement} duo
 * @param {HTMLInputElement} tier
 */
function contentsFor(duo, tier) {
  const colour = duo.dataset.colour || '';
  if (colour) return `${colour}:${parseInt(tier.dataset.quantity || '1', 10) || 1}`;
  return duo.dataset.contents || '';
}

/**
 * Contraintes à appliquer aux options du produit de pack.
 *
 * On ne recopie pas aveuglément les options cochées sur la fiche : le produit de
 * pack peut porter une option de même nom mais de valeurs différentes. Sur le
 * Pack de 2, `Coloris` a longtemps valu « Noir + Blanc », jamais « Noir » —
 * reporter le coloris de la fiche ne trouverait aucune variante.
 *
 * La règle est donc : une option n'est contrainte que si la valeur voulue existe
 * réellement parmi ses valeurs. Ce qui rend la correspondance insensible aux
 * noms d'options choisis dans l'admin.
 *
 * @param {PackMap} map
 * @param {HTMLInputElement} tier
 * @returns {Map<string, string>}
 */
function resolveWanted(map, tier) {
  const fromPicker = selectedOptions();
  // Le coloris de la vignette, et non son libellé : le libellé est une copy
  // libre, le coloris est la valeur exacte de l'option. Le libellé reste en
  // repli pour les vignettes à contenu fixe, qui n'ont pas de coloris.
  const picked = tier.dataset.selectionMode === 'duo' ? selectedDuo() : null;
  const duo = picked?.dataset.colour || picked?.dataset.label || '';
  const wanted = new Map();

  map.options.forEach((optionName, index) => {
    const values = new Set(map.variants.map((variant) => normalise(variant.options[index])));

    // Le duo prime sur le coloris de la fiche, qui désigne autre chose.
    if (duo && values.has(normalise(duo))) {
      wanted.set(normalise(optionName), duo);
      return;
    }

    const picked = fromPicker.get(normalise(optionName));
    if (picked && values.has(normalise(picked))) wanted.set(normalise(optionName), picked);
  });

  return wanted;
}

/**
 * @param {PackMap} map
 * @param {Map<string, string>} wanted - clés normalisées
 * @returns {PackVariant | undefined}
 */
function findVariant(map, wanted) {
  return map.variants.find((variant) =>
    map.options.every((optionName, index) => {
      const expected = wanted.get(normalise(optionName));
      return expected === undefined || normalise(variant.options[index]) === normalise(expected);
    })
  );
}

/**
 * « Noir:2, Blanc:2, Beige:1 » → « 2X Noir - 2X Blanc - 1X Beige »
 *
 * La quantité est toujours écrite, même à 1 : c'est ce qui rend les paliers
 * comparables d'un coup d'œil, et ce qui lève l'ambiguïté sur un pack de 5 où
 * deux coloris sont doublés et le troisième non.
 * @param {string} raw
 */
function formatContents(raw) {
  return (raw || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const [name, count] = part.split(':').map((piece) => piece.trim());
      const quantity = parseInt(count ?? '1', 10);
      const n = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
      return `${n}X ${name}`;
    })
    .join(' - ');
}

/**
 * Ligne à envoyer, ou null si le chemin natif doit s'en charger.
 * @returns {PackLine | null}
 */
function buildLine() {
  const tier = selectedTier();
  // L'Unité vend le produit de la page : le thème le fait mieux que nous, avec
  // sa file d'attente, ses états de bouton et ses messages d'erreur.
  if (!tier || tier.dataset.hasProduct !== 'true') return null;

  const map = readPackMap(tier);
  if (!map) return null;

  const duo = tier.dataset.selectionMode === 'duo' ? selectedDuo() : null;
  if (tier.dataset.selectionMode === 'duo' && !duo) return null;

  const variant = findVariant(map, resolveWanted(map, tier));
  if (!variant) return null;

  /** @type {PackLine} */
  const line = { id: variant.id, quantity: 1 };

  // Contenu en propriété visible : sur un produit simple, contrairement à un
  // bundle, rien n'écrit les coloris dans la commande. Visible et non masquée —
  // elle rassure le client au checkout autant qu'elle informe l'expédition.
  //
  // Écrite sur tous les paliers de pack, duo compris : les produits de pack ne
  // sont plus déclinés qu'en taille, et c'est la seule trace du coloris.
  const contents = formatContents(duo ? contentsFor(duo, tier) : tier.dataset.contents || '');
  if (contents) line.properties = { Contenu: contents };

  return line;
}

/**
 * Envoie des lignes au panier, en une seule requête, et prévient le thème.
 *
 * Une seule requête pour toutes les lignes d'une composition : Shopify les
 * ajoute toutes ou n'en ajoute aucune, et le panier ne reste jamais avec un
 * pack à moitié ajouté.
 *
 * La promesse rejette si Shopify refuse l'ajout — stock, variante retirée —
 * pour que l'appelant puisse le dire. Le panier est rechargé dans tous les cas,
 * les abonnés de l'événement doivent recevoir un état juste.
 * @param {PackLine[]} lines
 * @param {EventTarget} [source] - D'où part l'événement de panier. Il remonte
 *   jusqu'au document dans tous les cas ; émis depuis une fenêtre modale, il
 *   dit au tiroir de panier d'attendre qu'elle se ferme avant de s'ouvrir.
 * @returns {Promise<void>}
 */
function addToCart(lines, source = document) {
  const deferred = CartLinesUpdateEvent.createPromise();
  /** @type {Error | null} */
  let failure = null;
  /** @type {Record<string, string> | undefined} */
  let sections;

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
      promise: deferred.promise,
    })
  );

  // Le rendu du panier est demandé avec l'ajout, comme le fait Horizon
  // (assets/product-form.js) : le tiroir se redessine avec, sans refaire sa
  // propre requête. Mesuré le 2026-09-28 sur un Pack de 2 : 357 ms de moins
  // sur 1,1 s, davantage sur mobile.
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean);

  return fetch(`${window.Theme?.routes?.cart_add_url || '/cart/add'}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      items: lines,
      ...(sectionIds.length ? { sections: sectionIds.join(','), sections_url: window.location.pathname } : {}),
    }),
  })
    .then(async (response) => {
      if (response.ok) {
        const body = await response.json().catch(() => ({}));
        sections = body.sections || undefined;
        return;
      }
      const body = await response.json().catch(() => ({}));
      failure = new Error(body.description || body.message || '');
    })
    // La réponse de /cart/add ne porte que la ligne ajoutée. Les abonnés à
    // l'événement — pastille, tiroir, lignes de panier — attendent l'état
    // complet du panier : on va le chercher.
    .then(() => fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`))
    .then((response) => response.json())
    .then((ajaxCart) => {
      // Forme imposée par le module d'événements : `normalizeCartSummary` y
      // parcourt les lignes. Le convertisseur fourni est la seule façon fiable
      // de la produire — l'écrire à la main casse tous les abonnés d'un coup.
      deferred.resolve?.({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          itemCount: ajaxCart.item_count,
          source: 'pack-cart',
          sections,
        },
      });

      if (failure) throw failure;
    })
    .catch((error) => {
      deferred.reject?.(error);
      throw error;
    });
}

/**
 * Lignes d'une composition au panier : `_composition` les réunit en un seul
 * pack (snippets/cart-products.liquid) : « + » y est désactivé et « − » les
 * retire ensemble. Unique par ajout : deux compositions aux mêmes coloris ne
 * doivent pas fusionner en une ligne. Le « _ » le masque au client, dans le
 * panier comme au checkout.
 *
 * « Contenu » n'est écrit que si les coloris ne sont pas déjà portés par des
 * variantes séparées.
 * @param {{ id: number, quantity: number }[]} lines
 * @param {boolean} split
 * @param {string} contents
 * @returns {PackLine[]}
 */
function compositionItems(lines, split, contents) {
  const formatted = split ? '' : formatContents(contents || '');
  const composition = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

  return lines.map(({ id, quantity }) => ({
    id,
    quantity,
    properties: { ...(formatted ? { Contenu: formatted } : {}), _composition: composition },
  }));
}

/**
 * Pack composé dans les lignes par pièce (blocks/product-pack-pieces.liquid),
 * mélangé — coloris ou tailles — ou d'un nombre de pièces sans palier : facturé par le Pack perso,
 * une ligne par coloris, comme l'était la composition libre. Ses remises
 * automatiques comptent toutes ses pièces ensemble et donnent le montant de la
 * grille.
 *
 * Même règle que la composition libre (resolveLines, assets/pack-composer.js) :
 * un coloris introuvable est une erreur, jamais un repli, sinon la pièce
 * partirait dans un autre coloris.
 * @param {HTMLElement} pieces
 * @returns {PackLine[] | null}
 */
function buildMixedItems(pieces) {
  const node = pieces.querySelector('[data-pack-mix-map]');
  /** @type {PackMap | null} */
  let map = null;
  try {
    map = node?.textContent ? JSON.parse(node.textContent) : null;
  } catch {
    map = null;
  }

  /** @type {{ colour: string, size: string, quantity: number }[]} */
  let picked = [];
  try {
    picked = JSON.parse(pieces.dataset.lines || '[]');
  } catch {
    picked = [];
  }
  picked = picked.filter((line) => line.colour && line.size && line.quantity > 0);
  const contents = pieces.dataset.contents || '';
  if (!map || !picked.length) return null;

  /** @param {PackVariant} variant @param {string} value */
  const has = (variant, value) => variant.options.some((option) => normalise(option) === normalise(value));

  // Pack perso décliné en taille seulement : une ligne par taille, et les
  // coloris dans « Contenu ».
  if (map.options.length < 2) {
    /** @type {Map<number, number>} */
    const bySize = new Map();
    for (const line of picked) {
      const variant = map.variants.find((candidate) => has(candidate, line.size));
      if (!variant?.available) return null;
      bySize.set(variant.id, (bySize.get(variant.id) || 0) + line.quantity);
    }
    return compositionItems([...bySize].map(([id, quantity]) => ({ id, quantity })), false, contents);
  }

  // Une ligne par couple taille × coloris : chaque pièce a sa variante.
  const lines = [];
  for (const line of picked) {
    const variant = map.variants.find((candidate) => has(candidate, line.size) && has(candidate, line.colour));
    if (!variant?.available) return null;
    lines.push({ id: variant.id, quantity: line.quantity });
  }
  return compositionItems(lines, true, contents);
}

/**
 * Pack en un seul coloris, au nombre de pièces exact de son palier : la
 * variante du produit de pack, comme avant les lignes par pièce.
 *
 * La correspondance se fait sur les valeurs, jamais sur les noms d'options,
 * qui varient d'un produit de pack à l'autre (« Coloris », « Couleur »). Un
 * produit de pack décliné en coloris qui n'a pas celui-ci est une erreur,
 * jamais un repli : le pack partirait dans un autre coloris.
 * @param {HTMLInputElement} tier
 * @param {string} colour
 * @param {string} size
 * @param {number} count
 * @returns {PackLine | null}
 */
function buildUniformLine(tier, colour, size, count) {
  const map = readPackMap(tier);
  if (!map) return null;

  /** @type {Map<string, string>} */
  const wanted = new Map();
  let colourFound = false;
  map.options.forEach((optionName, index) => {
    const values = new Set(map.variants.map((variant) => normalise(variant.options[index])));
    if (values.has(normalise(colour))) {
      wanted.set(normalise(optionName), colour);
      colourFound = true;
    } else if (values.has(normalise(size))) {
      wanted.set(normalise(optionName), size);
    }
  });
  if (!colourFound && map.options.length > 1) return null;

  const variant = findVariant(map, wanted);
  if (!variant?.available) return null;

  return { id: variant.id, quantity: 1, properties: { Contenu: formatContents(`${colour}:${count}`) } };
}

/**
 * Lignes de panier des lignes par pièce, pour plus d'une pièce : le produit du
 * palier si le pack en a exactement le nombre, en un seul coloris et une seule
 * taille ; le Pack perso sinon — mélange de coloris ou de tailles, ou nombre de
 * pièces sans palier.
 * @param {HTMLElement} pieces
 * @param {number} count
 * @returns {PackLine[] | null}
 */
function buildPiecesItems(pieces, count) {
  const tier = selectedTier();
  const quantity = parseInt(tier?.dataset.quantity || '1', 10) || 1;

  if (tier && pieces.dataset.mixed !== 'true' && tier.dataset.hasProduct === 'true' && quantity === count) {
    /** @type {{ colour: string, size: string }[]} */
    let lines = [];
    try {
      lines = JSON.parse(pieces.dataset.lines || '[]');
    } catch {
      lines = [];
    }
    const [only] = lines;
    const line = only ? buildUniformLine(tier, only.colour, only.size, count) : null;
    return line ? [line] : null;
  }

  return buildMixedItems(pieces);
}

function handleSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (form.dataset.type !== 'add-to-cart-form') return;

  // Lignes par pièce (blocks/product-pack-pieces.liquid) : c'est leur contenu
  // qui décide du produit facturé.
  const pieces = document.querySelector('pack-pieces');
  if (pieces instanceof HTMLElement && pieces.dataset.count) {
    const count = parseInt(pieces.dataset.count, 10) || 1;

    // Une pièce : la variante de la fiche, dont le coloris suit celui de la
    // ligne. Le thème l'ajoute mieux que nous.
    if (count <= 1) return;

    // 🛑 Au-delà d'une pièce, on arrête l'envoi natif même si la ligne est
    // introuvable. Le laisser passer mettrait au panier une seule pièce, au
    // prix de l'Unité, sous un bouton qui annonce le prix du pack.
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const error = pieces.querySelector('[data-pieces-error]');
    /** @param {string} message */
    const showError = (message) => {
      if (!(error instanceof HTMLElement)) return;
      error.textContent = message;
      error.hidden = message === '';
    };

    const items = buildPiecesItems(pieces, count);
    if (!items) {
      showError(error instanceof HTMLElement ? error.dataset.labelMissing || '' : '');
      return;
    }

    showError('');
    addToCart(items).catch((failure) => showError(failure instanceof Error ? failure.message : ''));
    return;
  }

  // Sans lignes par pièce : le chemin des paliers et des vignettes de coloris.
  const line = buildLine();
  if (!line) return;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  // L'échec est déjà remis au thème par l'événement : rien à ajouter ici.
  addToCart([line]).catch(() => {});
}

/**
 * Lignes de la composition libre : Pack perso, une ligne par coloris, à la
 * quantité de ce coloris. Le total des pièces porte le prix par pièce, via les
 * remises automatiques du Pack perso, qui comptent toutes ses lignes ensemble.
 *
 * Une ligne par coloris, parce que DSers relie une variante à une variante
 * AliExpress et ne lit pas les propriétés : « Contenu » ne lui dit rien. Tant
 * que le Pack perso n'est décliné qu'en taille, le panneau envoie une seule
 * ligne (`split` faux), et « Contenu » reste la seule trace des coloris.
 * @param {Event} event
 */
function handleComposition(event) {
  if (!(event instanceof CustomEvent) || !event.detail) return;

  const { lines, split, contents, resolve, reject } = event.detail;
  event.detail.handled = true;

  const items = compositionItems(lines || [], Boolean(split), contents || '');

  // Le panneau de composition émet depuis lui-même : l'ajout part de sa
  // fenêtre, pas du document.
  addToCart(items, event.target instanceof Element ? event.target : document).then(resolve, reject);
}

window.addEventListener('submit', handleSubmit, true);
document.addEventListener('pack:add', handleComposition);
